import { z } from 'zod';

// ─── Inventory / production enums ──────────────────────────────────────

/** 6 канонических типов Stock Entry (ERPNext-derived; X1). */
export const StockEntryType = z.enum([
  'material_receipt',
  'material_issue',
  'material_transfer',
  'manufacture',
  'repack',
  'material_transfer_for_manufacture',
  /**
   * Компенсирующий документ отмены завершённого заказа (`createCompensatingManufactureSe`).
   * Создаётся только системой; в мастере документа его нет. Был потерян CHECK-ом миграции
   * 0128 (B36) — отмена завершённого заказа падала на вставке (найдено в B73 Ф6).
   */
  'manufacture_reversal',
]);
export type StockEntryType = z.infer<typeof StockEntryType>;

/** Тип item по товарной роли. */
export const ItemType = z.enum([
  'raw_material',
  'sub_assembly',
  'finished_good',
  'consumable',
  'service',
]);
export type ItemType = z.infer<typeof ItemType>;

/** Уровень иерархии в L1/L2 terminology (STAITER operational vs 1C regulatory peer). */
export const HierarchyLevel = z.enum(['l1_operational', 'l1_regulatory', 'l2']);
export type HierarchyLevel = z.infer<typeof HierarchyLevel>;

/** Метод валюации запаса. */
export const ValuationMethod = z.enum(['moving_avg', 'fifo', 'lifo']);
export type ValuationMethod = z.infer<typeof ValuationMethod>;

/** QC статус батча. */
export const QcStatus = z.enum(['pending', 'passed', 'failed', 'quarantine']);
export type QcStatus = z.infer<typeof QcStatus>;

// ─── Integration enums ────────────────────────────────────────────────

export const IntegrationHealth = z.enum(['ok', 'degraded', 'disconnected']);
export type IntegrationHealth = z.infer<typeof IntegrationHealth>;

export const IntegrationSyncMode = z.enum(['read', 'read_write']);
export type IntegrationSyncMode = z.infer<typeof IntegrationSyncMode>;

/** Источник данных (discriminator в canonical таблицах). */
export const SourceSystem = z.enum([
  'staiter',
  '1c',
  'bitrix',
  'adesk',
  'excel',
  'manual',
  // C.4 — bank statement import (XLSX/TXT).
  'bank_import',
  // S-FIX-3 hotfix: prod has 1636 payments seeded with this value (synthesised
  // year of demo data). Backward-compat — actual semantic is 'adesk' import.
  // Migration to rename in DB tracked separately.
  'adesk_seed',
  // S-FIX-11-SEED — baseline seed для S-FIX-7-A walkthrough (CP onec_ref, items,
  // boms, batches, drafts, payments, settlements, approvals, runs).
  'prod_baseline_seed',
]);
export type SourceSystem = z.infer<typeof SourceSystem>;

// ─── Conflict / anomaly enums ──────────────────────────────────────────

export const ConflictType = z.enum([
  'intra_level',
  'master_data_drift',
  'bom_explosion_loop',
  'valuation_mismatch',
  'temporal_precedence_unresolved',
  'reconciliation_variance',
  'tolerance_exceeded',
  'manual_override_deferred',
  // S-FIX-4: prod uses these from master-data sync + finance reconciliation.
  'master_data_field_mismatch',
  'transaction_amount_mismatch',
]);
export type ConflictType = z.infer<typeof ConflictType>;

export const AnomalyType = z.enum([
  'qty_zscore_outlier',
  'valuation_rate_jump',
  'bom_yield_variance',
  'cost_layer_imbalance',
  'velocity_drop',
  'duplicate_voucher',
  'orphan_movement',
  // S-FIX-4: prod data has these from finance + reconciliation engines.
  'overdue_ap',
  'overdue_ar',
  'reconciliation_variance',
  'fx_rate_jump',
  'cash_flow_negative',
  'scrap_above_threshold',
  'negative_stock',
  'duplicate_payment_candidate',
  // U-4 (integrations F7): продюсер — `integrations/pipeline.ts`; значения в enum не было,
  // и /anomalies печатал сырой ключ (найдено B23 пака `tails-closeout`).
  'counterparty_missing_bin',
  // B23 (WHS-B3): расход проведён по нулевой оценке — продюсер submit складского документа.
  'valuation_rate_missing',
]);
export type AnomalyType = z.infer<typeof AnomalyType>;

export const AnomalySeverity = z.enum([
  'info',
  'warn',
  'major',
  'critical',
  // S-FIX-4: prod has alias 'warning' (BE handler returns this); accept as
  // synonym of 'warn' (FE label resolves both via i18n).
  'warning',
]);
export type AnomalySeverity = z.infer<typeof AnomalySeverity>;

// ─── Run / task enums ─────────────────────────────────────────────────

export const TaskType = z.enum([
  'inventory_reconcile',
  'writeoff_review',
  'cost_draft',
  'anomaly_investigation',
  'period_close_draft',
]);
export type TaskType = z.infer<typeof TaskType>;

export const RuntimeStatus = z.enum([
  'queued',
  'running',
  // FE-284 Pass 53: BE drift accept — runtime returns 'in_progress' для running
  // workflows. Canonical SSOT — `running`, но lifecycle BE состояние ≠ canonical.
  // Temp tolerance (как BE-J для business_status) до status-matrix.ts BE mapping.
  'in_progress',
  'waiting_approval',
  'waiting_external',
  'failed',
  'completed',
  'cancelled',
]);
export type RuntimeStatus = z.infer<typeof RuntimeStatus>;

export const BusinessStatus = z.enum([
  'completed_draft',
  'needs_review',
  'degraded',
  'blocked',
  'escalated',
]);
export type BusinessStatus = z.infer<typeof BusinessStatus>;

// BE-1 (2026-05-20): runs.business_status столбец содержит superset канонического
// `BusinessStatus` — лайфцикл-статусы (in_progress, pending_review, pending_external,
// completed, failed) попадают в ту же колонку из workflow updates. Канонический
// BusinessStatus оставлен как есть (status-matrix output contract). Run-level shapes
// (RunSummary, DashboardSummary recent_runs) используют RunBusinessStatus.
export const RunBusinessStatus = z.enum([
  // Canonical (status-matrix engine output)
  'completed_draft',
  'needs_review',
  'degraded',
  'blocked',
  'escalated',
  // Lifecycle (workflow state)
  'completed',
  'failed',
  'in_progress',
  'pending_review',
  'pending_external',
]);
export type RunBusinessStatus = z.infer<typeof RunBusinessStatus>;

// ─── Roles / RBAC ─────────────────────────────────────────────────────

/**
 * S-multi-1 BM-1-3 — MembershipRole: роль per (user, entity) binding.
 * Расширяет старый Role enum новой `company_admin` для multi-tenancy.
 * После завершения S-multi-2 (BM-2-8) старая `users.role` колонка дропается.
 *
 * `Role` ниже оставлен как deprecated alias на 1 deploy для backward-compat.
 */
export const MembershipRole = z.enum([
  'accountant',
  'reviewer',
  'sme',
  'warehouse_operator',
  'production_planner',
  'admin',
  'manager',
  'viewer',
  // B63 пака `tails-closeout` (В-6 + В-27, 15.09.2026): `project_manager` и `pm_admin`
  // удалены — почти копии `manager`, на проде 0 пользователей и 0 членств. Миграция
  // 0131 останавливает выкат, если роль где-то жива.
  // S-multi-1: company-scope admin (entity_id-bound vs «global admin» = is_super_admin).
  'company_admin',
  /**
   * B1 (service-role §2) — сервисный аккаунт интеграции.
   *
   * Отдельный ВИД СУБЪЕКТА, а не человек с урезанными правами. Отсюда всё
   * остальное: пустой дефолт модулей (забытый грант закрывает, а не открывает),
   * запрет записи как свойство роли, ранг ниже всех и невидимость в человеческих
   * списках. До неё сервис был `viewer` с четырьмя ручными строками грантов, три
   * из которых — запреты: конструкция fail-open, разваливающаяся на второй компании.
   */
  'service',
]);
export type MembershipRole = z.infer<typeof MembershipRole>;

/** S-multi-1 BM-1-3 — HoldingRole: роль per (user, holding) binding. */
export const HoldingRole = z.enum(['holding_admin', 'holding_viewer']);
export type HoldingRole = z.infer<typeof HoldingRole>;

/**
 * access-control (2026-06-22, S0/F0.2) — единица модульного доступа.
 * Конструктор прав: админ галочками вкл/выкл модули per-пользователь поверх базовой роли.
 * Карта route → Module — в BACKLOG-2026-06-22-access-control-frontend.md (F0.2).
 * Секция nav `finance` расщепляется на `finance` + `projects` + `tasks`.
 */
export const Module = z.enum([
  'agent_review',
  'inventory',
  'production',
  'finance',
  'projects',
  'tasks',
  'reporting',
  'admin',
]);
export type Module = z.infer<typeof Module>;

/** Все модули в каноническом порядке (для итерации в resolution-engine + admin-UI). */
export const ALL_MODULES = Module.options;

/** S-multi-1 BM-1-3 — MembershipStatus. */
export const MembershipStatus = z.enum(['active', 'invited', 'disabled']);
export type MembershipStatus = z.infer<typeof MembershipStatus>;

/**
 * @deprecated S-multi-1 BM-1-3 — use `MembershipRole` instead.
 * Оставлен на 1 deploy для backward-compat — будет удалён в BM-S-multi-2-8.
 */
export const Role = MembershipRole;
export type Role = MembershipRole;

// ─── Variance / WO warnings ───────────────────────────────────────────

/**
 * S-FIX-9-BE §4 — Variance reasons.
 * SYNC §1.2: hybrid approach — расширенный enum + variance_reason_notes optional
 * для свободного текста (детали). Прод-данные с русским freeform → 'other' + notes.
 */
export const VarianceReason = z.enum([
  'theft',
  'damage',
  'system_error',
  'measurement_error',
  'evaporation_shrinkage',
  'yield_loss',
  'scrap',
  'spoilage',
  'transfer_misposting',
  'physical_damage',
  'other',
]);
export type VarianceReason = z.infer<typeof VarianceReason>;

export const WoWarningType = z.enum([
  'insufficient_stock',
  'batch_expiry_imminent',
  'batch_expired',
  'bom_alternate_used',
  'bom_scrap_exceeded',
  'job_card_missing',
  'cost_variance_high',
  'no_active_bom_version',
]);
export type WoWarningType = z.infer<typeof WoWarningType>;

// ─── Per-entity scope for audit / export ──────────────────────────────

export const EntityType = z.enum([
  'item',
  'warehouse',
  'batch',
  'bom',
  'work_order',
  'stock_entry',
  'run',
  'landed_cost',
  // Phase 2 (9 new — S13/S14/S15/S16).
  'counterparty', // S14
  'invoice', // S14
  'payment', // S14
  'settlement', // S14
  'budget', // S16
  'regulatory_doc', // S13
  'cogs_snapshot', // S13
  'cash_flow_snapshot', // S15
  'pnl_snapshot', // S15
  // S17–S22 — Sales Order + Project Lifecycle.
  'sales_order',
  'project',
  'project_task',
  'project_milestone',
  'project_task_log',
]);
export type EntityType = z.infer<typeof EntityType>;

// ─── Export / job status ──────────────────────────────────────────────

export const ExportFormat = z.enum(['json', 'csv', 'xlsx']);
export type ExportFormat = z.infer<typeof ExportFormat>;

export const ExportStatus = z.enum(['queued', 'running', 'done', 'failed', 'cancelled']);
export type ExportStatus = z.infer<typeof ExportStatus>;

export const JobStatus = z.enum(['queued', 'running', 'done', 'failed', 'cancelled']);
export type JobStatus = z.infer<typeof JobStatus>;

// ─── Work Order signal mapping ────────────────────────────────────────

/** REST-пути используют kebab-case ('pause-external'); frontend маппит через toKebab() helper. */
export const WoSignalType = z.enum([
  'release',
  'start',
  'pause_external',
  'resume_external',
  'complete',
  'cancel',
]);
export type WoSignalType = z.infer<typeof WoSignalType>;

// ─── Approval task type (закрытый список для approval-matrix API + gate) ──

export const ApprovalTaskType = z.enum([
  'stock_entry_submit',
  'stock_entry_cancel',
  'stock_reconciliation_submit',
  'opening_stock_submit',
  'landed_cost_submit',
  'valuation_method_change',
  'bom_activate',
  'warehouse_move',
  'batch_transition',
  'items_variants_bulk',
  'bulk_import_commit',
  'bulk_invite',
  'wo_cancel',
  'wo_complete',
  'wo_reassign',
  'period_close_lock',
  'master_data_push_1c',
  'aggregates_push_1c',
  'regulatory_push_esf',
  'regulatory_push_kpn',
  // Phase 2 (10 new — S13/S14/S15/S16). Push AR/AP invoice / payment / settlement
  // переиспользуют baseline `aggregates_push_1c`; counterparty — master-data.
  'cogs_publish', // S13
  'counterparty_push_1c', // S14
  'ar_invoice_post', // S14
  'ap_invoice_post', // S14
  'payment_post', // S14
  'settlement_post', // S14
  'cash_flow_publish', // S15
  'pnl_publish', // S15
  'budget_activate', // S16
  'budget_lock', // S16
  // S17–S22 — Sales Order + Project lifecycle approval tiers.
  'sales_order_cancel', // SO cancel of confirmed+
  'project_complete', // stage active→completed
  'project_close', // stage completed→closed (двойной guard: closeout + approver)
  'project_cancel', // cancel of active+
  /**
   * B11 (project-actions-parity §3.2) — архивация и возврат из архива.
   *
   * Защита была перевёрнута относительно ущерба: отмена, которая с задачами не
   * делала ничего, шла под Tier 2 с подписью, а архивация — та, что архивирует
   * два десятка задач, — по одному подтверждению. При этом журнал архивации уже
   * писал `tier: 2`, то есть утверждал то, чего не было.
   */
  'project_archive',
  'project_restore',
  /**
   * B8 (project-plan-change §2.2) — перенос срока проекта.
   *
   * Без объявления в матрице `/approvers?action=project.plan_shift` отдавал бы
   * фолбэк-роли вместо кандидатов, объявленных для переноса, — ровно тот дефект,
   * который B11 прошлого пака чинил для архивации.
   */
  'project_plan_shift',
  'milestone_draft_invoice', // manual AR draft
  /**
   * B64 пака `tails-closeout` (Ф6, SOLUTION §9.1) — три новые строки уровня 3.
   * `documents_push_1c` — отправка документов, счетов, платежей и взаиморасчётов в 1С
   * (раньше делила строку агрегатов, хотя зоны у них разные); `onec_push_rollback` —
   * откат отправки, зоны берутся у исходной; `work_order_cancel_completed` — отмена
   * завершённого заказа, раньше жила второй матрицей в коде (`lib/approval-matrix.ts`).
   */
  'documents_push_1c',
  'onec_push_rollback',
  'work_order_cancel_completed',
]);
export type ApprovalTaskType = z.infer<typeof ApprovalTaskType>;

/**
 * B64 (Ф6, SOLUTION §9.1) — зоны ответственности подписи уровня 3. Подписывают два
 * разных человека из двух РАЗНЫХ зон; роли зон — только из матрицы.
 */
export const Tier3Zone = z.enum(['finance', 'management', 'production']);
export type Tier3Zone = z.infer<typeof Tier3Zone>;

// ─── User preferences (S8) ────────────────────────────────────────────

/** Закрытый enum допустимых ключей user preferences (S8.2b). */
export const UserPreferenceKey = z.enum([
  'theme',
  'sidebar_collapsed',
  'timezone',
  'density',
  'onboarding_complete',
  'onboarding_progress',
  'last_filters',
  'notifications_muted_until',
  'default_export_format',
  // SET-AUDIT-5 (pass3 settings audit) — granular notification toggles.
  'notifications_email_approvals',
  'notifications_email_conflicts',
  'notifications_email_anomalies',
  'notifications_digest_cadence',
  // 2026-06-08 — per-user sidebar layout (collapse / reorder / rename / custom sections / item DnD).
  'sidebar_layout',
]);
export type UserPreferenceKey = z.infer<typeof UserPreferenceKey>;

/** Ресурсы, для которых поддерживаются saved views (S8.2b). */
export const SavedViewResource = z.enum([
  'runs',
  'conflicts',
  'anomalies',
  'approvals',
  'work_orders',
  'stock_ledger',
  'stock_entries',
  'items',
  'batches',
  'boms',
]);
export type SavedViewResource = z.infer<typeof SavedViewResource>;

/** Memory scope (S8.4). */
export const MemoryScope = z.enum(['global', 'task', 'run']);
export type MemoryScope = z.infer<typeof MemoryScope>;

/** Memory sensitivity class. */
export const MemorySensitivity = z.enum(['public', 'internal', 'restricted', 'confidential']);
export type MemorySensitivity = z.infer<typeof MemorySensitivity>;

/** Memory lifecycle status. */
export const MemoryLifecycle = z.enum(['draft', 'active', 'superseded', 'expired']);
export type MemoryLifecycle = z.infer<typeof MemoryLifecycle>;

/** Integration health event type (S8.2). */
export const IntegrationHealthEventType = z.enum(['ok', 'degraded', 'disconnected']);
export type IntegrationHealthEventType = z.infer<typeof IntegrationHealthEventType>;

// ─── Currency ─────────────────────────────────────────────────────────

export const Currency = z.enum(['KZT', 'USD', 'EUR', 'RUB', 'CNY']);
export type Currency = z.infer<typeof Currency>;
